import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { salesOrderAPI, customerAPI, inventoryAPI, measurementAPI, feeAPI, discountAPI, vatAPI } from '../../services/api';
import { fabricSewingTypeAPI } from '../../services/fabricSewingTypeAPI';
import AlertModal from '../Common/AlertModal';
import MeasurementModal from './MeasurementModal';
import FeeSelectionModal from './FeeSelectionModal';
import DiscountSelectionModal from './DiscountSelectionModal';

const SalesOrderForm = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  // Prevent scroll wheel from changing number input values
  const handleNumberInputWheel = (e) => {
    e.currentTarget.blur();
  };
  const [formData, setFormData] = useState({
    customer_id: '',
    product_type: '',
    product_name: '',
    product_description: '',
    order_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    status: 'Pending',
    delivery_method: 'delivery',
    payment_method: 'cash',
    payment_status: 'unpaid',
    amount_paid: '',
    fee_id: '',
    discount_id: '',
    notes: '',
  });
  const [items, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState({
    inventory_item_id: '',
    quantity: '',
    unit_price: '',
    discount_id: '',
    fabric_sewing_type_id: '',
    notes: '',
  });
  const [customers, setCustomers] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [fees, setFees] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [fabricTypes, setFabricTypes] = useState([]);
  const [vat, setVat] = useState({ id: null, name: 'VAT', percentage: 0, is_active: false });
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'error' });
  const [measurementModal, setMeasurementModal] = useState({
    isOpen: false,
    itemIndex: null,
    itemName: ''
  });
  const [previousMeasurements, setPreviousMeasurements] = useState(null);
  const [feeModalOpen, setFeeModalOpen] = useState(false);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [itemDiscountModal, setItemDiscountModal] = useState({ isOpen: false, itemIndex: null });

  const showAlert = (title, message, type = 'error') => {
    setAlert({ isOpen: true, title, message, type });
  };

  const closeAlert = () => {
    setAlert({ isOpen: false, title: '', message: '', type: 'error' });
  };

  useEffect(() => {
    loadCustomers();
    loadInventoryItems();
    loadFees();
    loadDiscounts();
    loadFabricTypes();
    loadVat();
    if (id) loadOrder();
  }, [id]);

  useEffect(() => {
    if (formData.customer_id && !id) {
      loadPreviousMeasurements();
    }
  }, [formData.customer_id]);

  // Auto-fill amount paid when payment status is "paid"
  useEffect(() => {
    if (formData.payment_status === 'paid' || formData.payment_status === 'unpaid') {
      // Calculate totals inside the effect
      const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);

      const itemsDiscountTotal = items.reduce((sum, item) => {
        if (item.discount_id) {
          const discount = discounts.find(d => d.id === parseInt(item.discount_id));
          if (discount) {
            const itemSubtotal = item.quantity * item.unit_price;
            const discountAmount = discount.type === 'percentage'
              ? (itemSubtotal * parseFloat(discount.value) / 100)
              : parseFloat(discount.value);
            return sum + discountAmount;
          }
        }
        return sum;
      }, 0);

      let orderDiscountAmount = 0;
      if (formData.discount_id) {
        const orderDiscount = discounts.find(d => d.id === parseInt(formData.discount_id));
        if (orderDiscount) {
          orderDiscountAmount = orderDiscount.type === 'percentage'
            ? ((subtotal - itemsDiscountTotal) * parseFloat(orderDiscount.value) / 100)
            : parseFloat(orderDiscount.value);
        }
      }

      let feeAmount = 0;
      if (formData.fee_id) {
        const fee = fees.find(f => f.id === parseInt(formData.fee_id));
        if (fee) {
          feeAmount = parseFloat(fee.amount);
        }
      }

      // Calculate total including VAT
      const subjectToVat = subtotal - itemsDiscountTotal - orderDiscountAmount;
      const vatAmount = (subjectToVat * vat.percentage / 100);
      const total = (subjectToVat + vatAmount + feeAmount).toFixed(2);

      if (formData.payment_status === 'paid') {
        setFormData(prev => ({ ...prev, amount_paid: total }));
      } else if (formData.payment_status === 'unpaid') {
        setFormData(prev => ({ ...prev, amount_paid: '' }));
      }
    }
  }, [formData.payment_status, formData.discount_id, formData.fee_id, items, discounts, fees]);

  const loadCustomers = async () => {
    try {
      const response = await customerAPI.getAll();
      setCustomers(response.data.data || response.data);
    } catch (error) {
      console.error('Error loading customers');
    }
  };

  const loadInventoryItems = async () => {
    try {
      const response = await inventoryAPI.getAll();
      setInventoryItems(response.data.data || response.data);
    } catch (error) {
      console.error('Error loading inventory items');
    }
  };

  const loadFees = async () => {
    try {
      const response = await feeAPI.getAll({ is_active: true });
      setFees(response.data.data || response.data);
    } catch (error) {
      console.error('Error loading fees');
    }
  };

  const loadVat = async () => {
    try {
      const response = await vatAPI.get();
      const vatData = response.data.data;
      setVat({
        id: vatData.id,
        name: vatData.name || 'VAT',
        percentage: parseFloat(vatData.percentage || 0),
        is_active: vatData.is_active ?? true,
      });
    } catch (error) {
      console.error('Error loading VAT:', error);
      // Set default VAT if not found
      setVat({ id: null, name: 'VAT', percentage: 0, is_active: false });
    }
  };

  const loadDiscounts = async () => {
    try {
      const response = await discountAPI.getAll({ is_active: true });
      setDiscounts(response.data.data || response.data);
    } catch (error) {
      console.error('Error loading discounts');
    }
  };

  const loadFabricTypes = async () => {
    try {
      const response = await fabricSewingTypeAPI.getActive();
      setFabricTypes(response.data.data || response.data || []);
    } catch (error) {
      console.error('Error loading fabric sewing types', error);
      setFabricTypes([]);
    }
  };

  const getItemName = (item) => {
    return i18n.language === 'ar' && item.item_name_ar ? item.item_name_ar : item.item_name;
  };

  const loadOrder = async () => {
    try {
      const response = await salesOrderAPI.getOne(id);
      const data = response.data.data || response.data;
      setFormData({
        ...data,
        order_date: data.order_date?.split('T')[0] || data.order_date,
        expected_delivery_date: data.expected_delivery_date?.split('T')[0] || data.expected_delivery_date || '',
      });
      if (data.items && data.items.length > 0) {
        const mappedItems = data.items.map(item => ({
          ...item,
          measurements: item.measurement ? {
            length: item.measurement.length,
            chest: item.measurement.chest,
            waist: item.measurement.waist,
            hips: item.measurement.hips,
            shoulder: item.measurement.shoulder,
            sleeve: item.measurement.sleeve,
            neck: item.measurement.neck,
          } : null
        }));
        // ensure fabric_sewing_type_id is present on mapped items
        mappedItems.forEach(mi => {
          mi.fabric_sewing_type_id = mi.fabric_sewing_type_id || (mi.sales_order_item && mi.sales_order_item.fabric_sewing_type_id) || null;
        });
        setItems(mappedItems);
      }
    } catch (error) {
      showAlert(t('messages.error'), t('messages.failedToLoadOrder'), 'error');
    }
  };

  const loadPreviousMeasurements = async () => {
    try {
      const response = await measurementAPI.getLatestByCustomer(formData.customer_id);
      if (response.data.data) {
        setPreviousMeasurements(response.data.data);
      }
    } catch (error) {
      setPreviousMeasurements(null);
    }
  };

  const getInventoryItemInfo = (itemId) => {
    return inventoryItems.find(item => item.id === parseInt(itemId));
  };

  const handleAddItem = () => {
    if (!currentItem.inventory_item_id || currentItem.quantity <= 0 || currentItem.unit_price <= 0) {
      showAlert(t('salesOrders.validationError'), t('salesOrders.fillAllFields'), 'warning');
      return;
    }

    const inventoryItem = getInventoryItemInfo(currentItem.inventory_item_id);
    if (!inventoryItem) {
      showAlert(t('salesOrders.invalidItem'), t('salesOrders.itemNotValid'), 'error');
      return;
    }

    const requiresFabricType = inventoryItem?.unit?.toLowerCase().includes('meter');
    if (requiresFabricType && !currentItem.fabric_sewing_type_id) {
      showAlert(t('salesOrders.validationError'), t('messages.selectFabricSewingType'), 'warning');
      return;
    }

    const existingQuantity = items
      .filter(item => item.inventory_item_id === parseInt(currentItem.inventory_item_id))
      .reduce((sum, item) => sum + parseFloat(item.quantity), 0);

    const newTotalQuantity = existingQuantity + parseFloat(currentItem.quantity);

    if (newTotalQuantity > inventoryItem.quantity) {
      const message = t('salesOrders.insufficientMessage', {
        itemName: getItemName(inventoryItem),
        available: inventoryItem.quantity,
        unit: inventoryItem.unit,
        inOrder: existingQuantity,
        requested: parseFloat(currentItem.quantity),
        remaining: (inventoryItem.quantity - existingQuantity).toFixed(2)
      });
      showAlert(t('salesOrders.insufficientInventory'), message, 'error');
      return;
    }

    setItems([...items, {
      ...currentItem,
      quantity: parseFloat(currentItem.quantity),
      unit_price: parseFloat(currentItem.unit_price),
      discount_id: currentItem.discount_id || null,
      inventory_item: inventoryItem,
      fabric_sewing_type_id: currentItem.fabric_sewing_type_id || null,
      measurements: null
    }]);

    setCurrentItem({
      inventory_item_id: '',
      quantity: '',
      unit_price: '',
      discount_id: '',
      fabric_sewing_type_id: '',
      notes: '',
    });
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleOpenMeasurementModal = (index) => {
    const item = items[index];
    const inventoryItem = item.inventory_item || getInventoryItemInfo(item.inventory_item_id);
    setMeasurementModal({
      isOpen: true,
      itemIndex: index,
      itemName: getItemName(inventoryItem)
    });
  };

  const handleCloseMeasurementModal = () => {
    setMeasurementModal({
      isOpen: false,
      itemIndex: null,
      itemName: ''
    });
  };

  const handleSaveMeasurements = (measurements) => {
    const updatedItems = [...items];
    updatedItems[measurementModal.itemIndex] = {
      ...updatedItems[measurementModal.itemIndex],
      measurements: measurements
    };
    setItems(updatedItems);
    console.log(items);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (items.length === 0) {
      showAlert(t('salesOrders.noItems'), t('salesOrders.addAtLeastOneItem'), 'warning');
      return;
    }

    // Check for critical lab tasks before editing
    if (id) {
      try {
        const labTasksResponse = await fetch(`/api/lab?sales_order_id=${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (labTasksResponse.ok) {
          const labTasks = await labTasksResponse.json();
          const criticalTasks = (labTasks.data || []).filter(task =>
            ['In Progress', 'Completed'].includes(task.status)
          );

          if (criticalTasks.length > 0) {
            showAlert(
              t('messages.warning'),
              `Cannot edit order with ${criticalTasks.length} in-progress or completed lab tasks. Please create a new order instead.`,
              'warning'
            );
            return;
          }
        }
      } catch (error) {
        console.error('Error checking lab tasks:', error);
      }
    }

    setLoading(true);

    const submitData = {
      ...formData,
      fee_id: formData.fee_id || null,
      discount_id: formData.discount_id || null,
      amount_paid: formData.amount_paid ? parseFloat(formData.amount_paid) : null,
      items: items.map(item => ({
        // Include item id when editing so backend updates instead of recreating
        id: item.id || undefined,
        inventory_item_id: item.inventory_item_id,
        quantity: item.quantity,
        unit_price: item.unit_price,
        discount_id: item.discount_id || null,
        notes: item.notes,
        measurements: item.measurements,
        fabric_sewing_type_id: item.fabric_sewing_type_id || null
      }))
    };

    // Debug: log payload so we can verify fabric_sewing_type_id is included
    // Remove or comment out in production
    // eslint-disable-next-line no-console
    console.log('Submitting SalesOrder payload:', JSON.parse(JSON.stringify(submitData)));

    try {
      if (id) {
        await salesOrderAPI.update(id, submitData);
      } else {
        await salesOrderAPI.create(submitData);
      }
      navigate('/sales-orders');
    } catch (error) {
      if (error.response?.data?.message) {
        showAlert(t('messages.error'), error.response.data.message, 'error');
      } else {
        showAlert(t('messages.error'), t('messages.failedToSaveOrder'), 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Calculate subtotal (robust numeric parsing)
  const subtotal = items.reduce((sum, item) => {
    const qty = parseFloat(item.quantity || 0);
    const price = parseFloat(item.unit_price || 0);
    return sum + (isNaN(qty) || isNaN(price) ? 0 : qty * price);
  }, 0);

  // Calculate item discounts
  const itemsDiscountTotal = items.reduce((sum, item) => {
    if (item.discount_id) {
      const discount = discounts.find(d => d.id === parseInt(item.discount_id));
      if (discount) {
        const itemSubtotal = (parseFloat(item.quantity || 0) * parseFloat(item.unit_price || 0)) || 0;
        const value = parseFloat(discount.value || 0);
        const discountAmount = discount.type === 'percentage'
          ? (itemSubtotal * (isNaN(value) ? 0 : value) / 100)
          : (isNaN(value) ? 0 : value);
        return sum + discountAmount;
      }
    }
    return sum;
  }, 0);

  // Calculate order discount
  let orderDiscountAmount = 0;
  if (formData.discount_id) {
    const orderDiscount = discounts.find(d => d.id === parseInt(formData.discount_id));
    if (orderDiscount) {
      const value = parseFloat(orderDiscount.value || 0);
      orderDiscountAmount = orderDiscount.type === 'percentage'
        ? (((subtotal - itemsDiscountTotal) * (isNaN(value) ? 0 : value)) / 100)
        : (isNaN(value) ? 0 : value);
    }
  }

  // Get fee amount
  let feeAmount = 0;
  if (formData.fee_id) {
    const fee = fees.find(f => f.id === parseInt(formData.fee_id));
    if (fee) {
      const val = parseFloat(fee.amount || 0);
      feeAmount = isNaN(val) ? 0 : val;
    }
  }

  // Calculate VAT (applied on subtotal after discounts)
  const subjectToVat = subtotal - itemsDiscountTotal - orderDiscountAmount;
  const vatAmount = (subjectToVat * vat.percentage / 100);

  // Calculate total (subtotal - discounts + fee + vat)
  const totalPrice = (subjectToVat + vatAmount + feeAmount).toFixed(2);

  // Calculate remaining amount
  const amountPaid = parseFloat(formData.amount_paid || 0);
  const remainingAmount = (parseFloat(totalPrice) - amountPaid).toFixed(2);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/sales-orders')}
            className="mb-4 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('common.back')}
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                {id ? t('salesOrders.editOrder') : t('salesOrders.addOrder')}
              </h1>
              <p className="text-gray-600">{t('salesOrders.subtitle')}</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm text-gray-500">{t('salesOrders.totalPrice')}</p>
                <p className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  {t('common.currency')} {totalPrice}
                </p>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Information Card */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
                <h2 className="text-xl font-semibold text-white flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {t('salesOrders.customer_information')}
                </h2>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('salesOrders.customer')} *
                  </label>
                  <select
                    value={formData.customer_id}
                    onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  >
                    <option value="">{t('salesOrders.select_customer')}</option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name} - {customer.phone}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Items Section Card */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-green-600 to-teal-600 px-6 py-4">
                <h2 className="text-xl font-semibold text-white flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  {t('salesOrders.orderItems')} ({items.length})
                </h2>
              </div>
              <div className="p-6">
                {/* Add Item Form */}
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 mb-6 border-2 border-blue-100">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    {t('salesOrders.addItem')}
                  </h3>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {t('salesOrders.item')} *
                        </label>
                        <select
                          value={currentItem.inventory_item_id}
                          onChange={(e) => {
                            const item = getInventoryItemInfo(e.target.value);
                            setCurrentItem({
                              ...currentItem,
                              inventory_item_id: e.target.value,
                              unit_price: item?.cost_per_unit || ''
                            });
                          }}
                          className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        >
                          <option value="">{t('salesOrders.selectItem')}</option>
                          {inventoryItems.map((item) => (
                            <option key={item.id} value={item.id}>
                              {getItemName(item)} ({item.item_code}) - Stock: {item.quantity} {item.unit}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {t('salesOrders.quantity')} *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={currentItem.quantity}
                          onChange={(e) => setCurrentItem({ ...currentItem, quantity: e.target.value })}
                          onWheel={handleNumberInputWheel}
                          className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&]:[-moz-appearance:textfield]"
                          placeholder="0.00"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {t('salesOrders.unitPrice')} *
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-3 text-gray-500">{t('common.currency')}</span>
                          <input
                            type="number"
                            step="0.01"
                            value={currentItem.unit_price}
                            onChange={(e) => setCurrentItem({ ...currentItem, unit_price: e.target.value })}
                            onWheel={handleNumberInputWheel}
                            className="w-full pl-20 pr-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&]:[-moz-appearance:textfield]"
                            placeholder="0.00"
                          />
                        </div>
                      </div>

                      {/* Fabric Sewing Type (only shown for meter-based items) */}
                      {(() => {
                        const inv = getInventoryItemInfo(currentItem.inventory_item_id);
                        const needsFabric = inv?.unit?.toLowerCase().includes('meter');
                        if (!needsFabric) return null;
                        return (
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">{t('fabricSewingTypes.title')}</label>
                            <select
                              value={currentItem.fabric_sewing_type_id}
                              onChange={(e) => setCurrentItem({ ...currentItem, fabric_sewing_type_id: e.target.value })}
                              className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            >
                              <option value="">{t('messages.selectFabricSewingType')}</option>
                              {fabricTypes.map(ft => (
                                <option key={ft.id} value={ft.id}>{ft.name} ({ft.code})</option>
                              ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">{t('fabricSewingTypes.requiredForMeters')}</p>
                          </div>
                        );
                      })()}

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {t('salesOrders.item_discount')}
                        </label>
                        <button
                          type="button"
                          onClick={() => setItemDiscountModal({ isOpen: true, itemIndex: -1 })}
                          className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl hover:border-orange-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all text-left flex items-center justify-between"
                        >
                          <span className={currentItem.discount_id ? 'text-gray-900' : 'text-gray-500'}>
                            {currentItem.discount_id ? (
                              (() => {
                                const discount = discounts.find(d => d.id === parseInt(currentItem.discount_id));
                                if (!discount) return t('discounts.selectDiscount');
                                return `${i18n.language === 'ar' && discount.name_ar ? discount.name_ar : discount.name} (${discount.type === 'percentage' ? `${discount.value}%` : `${t('common.currency')} ${discount.value}`})`;
                              })()
                            ) : t('discounts.selectDiscount')}
                          </span>
                          <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                          </svg>
                        </button>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {t('common.notes')}
                        </label>
                        <input
                          type="text"
                          value={currentItem.notes}
                          onChange={(e) => setCurrentItem({ ...currentItem, notes: e.target.value })}
                          className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          placeholder={t('salesOrders.notesPlaceholder')}
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleAddItem}
                      className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl flex items-center justify-center"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      {t('salesOrders.addItem')}
                    </button>
                  </div>
                </div>

                {/* Items List */}
                {items.length > 0 ? (
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('salesOrders.addedItems')}</h3>
                    {items.map((item, index) => {
                      const inventoryItem = item.inventory_item || getInventoryItemInfo(item.inventory_item_id);
                      const requiresMeasurements = inventoryItem?.unit?.toLowerCase().includes('meter');
                      const hasMeasurements = item.measurements && Object.values(item.measurements).some(val => val !== '');

                      return (
                        <div key={index} className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border-2 border-gray-200 hover:border-blue-300 transition-all">
                          <div className="flex items-center justify-between">
                            <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-4">
                              <div className="md:col-span-2">
                                <p className="text-xs text-gray-500 mb-1">{t('salesOrders.item')}</p>
                                <p className="font-semibold text-gray-900">
                                  {inventoryItem ? getItemName(inventoryItem) : ''}
                                  {inventoryItem?.deleted_at && (
                                    <span className="ml-2 text-xs font-normal text-red-600 bg-red-50 px-2 py-0.5 rounded">{t('common.deleted')}</span>
                                  )}
                                </p>
                                <p className="text-sm text-gray-600">{inventoryItem?.item_code}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-1">{t('salesOrders.qtyPrice')}</p>
                                <p className="font-semibold text-gray-900">{item.quantity} {inventoryItem?.unit}</p>
                                <p className="text-xs text-gray-600">@ {t('common.currency')} {parseFloat(item.unit_price).toFixed(2)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-1">{t('salesOrders.discount')}</p>
                                <p className="font-semibold text-orange-600">
                                  {(() => {
                                    if (!item.discount_id) return '-';
                                    const discount = discounts.find(d => d.id === parseInt(item.discount_id));
                                    if (!discount) return '-';
                                    const itemSubtotal = item.quantity * item.unit_price;
                                    const discountAmount = discount.type === 'percentage'
                                      ? (itemSubtotal * parseFloat(discount.value) / 100)
                                      : parseFloat(discount.value);
                                    return `- ${t('common.currency')} ${parseFloat(discountAmount).toFixed(2)}`;
                                  })()}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-1">{t('accounting.subtotal')}</p>
                                <p className="font-semibold text-green-600">
                                  {(() => {
                                    const itemSubtotal = item.quantity * item.unit_price;
                                    let discountAmount = 0;
                                    if (item.discount_id) {
                                      const discount = discounts.find(d => d.id === parseInt(item.discount_id));
                                      if (discount) {
                                        discountAmount = discount.type === 'percentage'
                                          ? (itemSubtotal * parseFloat(discount.value) / 100)
                                          : parseFloat(discount.value);
                                      }
                                    }
                                    return `${t('common.currency')} ${(itemSubtotal - discountAmount).toFixed(2)}`;
                                  })()}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 ml-4">
                              {requiresMeasurements && (
                                <button
                                  type="button"
                                  onClick={() => handleOpenMeasurementModal(index)}
                                  className={`p-2 rounded-lg transition-colors ${
                                    hasMeasurements
                                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                  }`}
                                  title={hasMeasurements ? t('salesOrders.viewEditMeasurements') : t('salesOrders.addMeasurements')}
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                  </svg>
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => handleRemoveItem(index)}
                                className="p-2 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg transition-colors"
                                title={t('messages.removeItem')}
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                          {item.notes && (
                            <div className="mt-3 pt-3 border-t border-gray-300">
                              <p className="text-sm text-gray-600">
                                <span className="font-semibold">{t('common.notes')}:</span> {item.notes}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    <p>{t('salesOrders.noItemsYet')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Order Details & Summary */}
          <div className="lg:col-span-1 space-y-6">
            {/* Order Details Card */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden sticky top-6">
              <div className="bg-gradient-to-r from-orange-500 to-red-500 px-6 py-4">
                <h2 className="text-xl font-semibold text-white flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {t('salesOrders.orderDetails')}
                </h2>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('salesOrders.orderDate')} *
                  </label>
                  <input
                    type="date"
                    value={formData.order_date}
                    onChange={(e) => setFormData({ ...formData, order_date: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('salesOrders.expectedDeliveryDate')}
                  </label>
                  <input
                    type="date"
                    value={formData.expected_delivery_date}
                    onChange={(e) => setFormData({ ...formData, expected_delivery_date: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('common.status')} *
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  >
                    <option value="Pending">{t('salesOrders.statuses.Pending')}</option>
                    <option value="In Production">{t('salesOrders.statuses.In Production')}</option>
                    <option value="Ready">{t('salesOrders.statuses.Ready')}</option>
                    <option value="Delivered">{t('salesOrders.statuses.Delivered')}</option>
                    <option value="Cancelled">{t('salesOrders.statuses.Cancelled')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('salesOrders.deliveryMethod')} *
                  </label>
                  <select
                    value={formData.delivery_method}
                    onChange={(e) => setFormData({ ...formData, delivery_method: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  >
                    <option value="delivery">{t('salesOrders.deliveryMethods.delivery')}</option>
                    <option value="pickup">{t('salesOrders.deliveryMethods.pickup')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('salesOrders.paymentMethod')} *
                  </label>
                  <select
                    value={formData.payment_method}
                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  >
                    <option value="cash">{t('salesOrders.paymentMethods.cash')}</option>
                    <option value="card">{t('salesOrders.paymentMethods.card')}</option>
                    <option value="transfer">{t('salesOrders.paymentMethods.transfer')}</option>
                    <option value="other">{t('salesOrders.paymentMethods.other')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('salesOrders.paymentStatus')} *
                  </label>
                  <select
                    value={formData.payment_status}
                    onChange={(e) => setFormData({ ...formData, payment_status: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  >
                    <option value="unpaid">{t('salesOrders.paymentStatuses.unpaid')}</option>
                    <option value="partial">{t('salesOrders.paymentStatuses.partial')}</option>
                    <option value="paid">{t('salesOrders.paymentStatuses.paid')}</option>
                  </select>
                </div>

                {formData.payment_status !== 'unpaid' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('salesOrders.amountPaid')} *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">{t('common.currency')}</span>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.amount_paid}
                        onChange={(e) => setFormData({ ...formData, amount_paid: e.target.value })}
                        onWheel={handleNumberInputWheel}
                        required={formData.payment_status !== 'unpaid'}
                        max={totalPrice}
                        className="w-full pl-20 pr-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&]:[-moz-appearance:textfield]"
                        placeholder="0.00"
                        readOnly={formData.payment_status === 'paid'}
                      />
                    </div>
                    {formData.payment_status === 'partial' && formData.amount_paid && (
                      <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-yellow-800 font-medium">{t('salesOrders.remaining')}:</span>
                          <span className="text-yellow-900 font-bold">
                            {t('common.currency')} {remainingAmount}
                          </span>
                        </div>
                      </div>
                    )}
                    {parseFloat(formData.amount_paid || 0) > parseFloat(totalPrice) && (
                      <p className="mt-2 text-sm text-red-600">{t('salesOrders.amountExceedsTotal')}</p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('salesOrders.orderDiscount')}
                  </label>
                  <button
                    type="button"
                    onClick={() => setDiscountModalOpen(true)}
                    className="w-full px-4 py-3 bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-200 rounded-xl hover:border-orange-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all text-left flex items-center justify-between group"
                  >
                    <span className={formData.discount_id ? 'text-gray-900 font-medium' : 'text-gray-500'}>
                      {formData.discount_id ? (
                        (() => {
                          const discount = discounts.find(d => d.id === parseInt(formData.discount_id));
                          if (!discount) return t('discounts.selectDiscount');
                          return `${i18n.language === 'ar' && discount.name_ar ? discount.name_ar : discount.name}`;
                        })()
                      ) : t('discounts.selectDiscount')}
                    </span>
                    <div className="flex items-center gap-2">
                      {formData.discount_id && (
                        (() => {
                          const discount = discounts.find(d => d.id === parseInt(formData.discount_id));
                          if (!discount) return null;
                          return (
                            <span className="text-orange-600 font-bold">
                              {discount.type === 'percentage' ? `${discount.value}%` : `${t('common.currency')} ${discount.value}`}
                            </span>
                          );
                        })()
                      )}
                      <svg className="w-5 h-5 text-orange-500 group-hover:text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                    </div>
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('salesOrders.fee')}
                  </label>
                  <button
                    type="button"
                    onClick={() => setFeeModalOpen(true)}
                    className="w-full px-4 py-3 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl hover:border-blue-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-left flex items-center justify-between group"
                  >
                    <span className={formData.fee_id ? 'text-gray-900 font-medium' : 'text-gray-500'}>
                      {formData.fee_id ? (
                        (() => {
                          const fee = fees.find(f => f.id === parseInt(formData.fee_id));
                          if (!fee) return t('fees.selectFee');
                          return `${i18n.language === 'ar' && fee.name_ar ? fee.name_ar : fee.name}`;
                        })()
                      ) : t('fees.selectFee')}
                    </span>
                    <div className="flex items-center gap-2">
                      {formData.fee_id && (
                        (() => {
                          const fee = fees.find(f => f.id === parseInt(formData.fee_id));
                          if (!fee) return null;
                          return (
                            <span className="text-blue-600 font-bold">
                              {t('common.currency')} {parseFloat(fee.amount).toFixed(2)}
                            </span>
                          );
                        })()
                      )}
                      <svg className="w-5 h-5 text-blue-500 group-hover:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('common.notes')}
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows="3"
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                    placeholder={t('salesOrders.notesPlaceholder')}
                  />
                </div>

                {/* Summary */}
                <div className="pt-4 border-t-2 border-gray-200">
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{t('salesOrders.itemsCount')}:</span>
                      <span className="font-semibold">{items.length}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{t('accounting.subtotal')}:</span>
                      <span className="font-semibold">{t('common.currency')} {subtotal.toFixed(2)}</span>
                    </div>
                    {itemsDiscountTotal > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-orange-600">{t('salesOrders.itemsDiscount')}:</span>
                        <span className="font-semibold text-orange-600">- {t('common.currency')} {itemsDiscountTotal.toFixed(2)}</span>
                      </div>
                    )}
                    {orderDiscountAmount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-orange-600">{t('salesOrders.orderDiscount')}:</span>
                        <span className="font-semibold text-orange-600">- {t('common.currency')} {orderDiscountAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {feeAmount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-blue-600">{t('salesOrders.fee')}:</span>
                        <span className="font-semibold text-blue-600">+ {t('common.currency')} {feeAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {vatAmount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-yellow-600 font-semibold">{vat.name} ({vat.percentage.toFixed(2)}%):</span>
                        <span className="font-semibold text-yellow-600">+ {t('common.currency')} {vatAmount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xl font-bold pt-2 border-t border-gray-300">
                      <span>{t('accounting.total')}:</span>
                      <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                        {t('common.currency')} {totalPrice}
                      </span>
                    </div>

                    {/* Payment Summary */}
                    {formData.payment_status !== 'unpaid' && formData.amount_paid && (
                      <div className="pt-3 mt-3 border-t border-gray-300">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-green-600">{t('salesOrders.amountPaid')}:</span>
                          <span className="font-semibold text-green-600">{t('common.currency')} {parseFloat(formData.amount_paid || 0).toFixed(2)}</span>
                        </div>
                        {formData.payment_status === 'partial' && (
                          <div className="flex justify-between text-sm">
                            <span className="text-red-600 font-medium">{t('salesOrders.remaining')}:</span>
                            <span className="font-bold text-red-600">{t('common.currency')} {remainingAmount}</span>
                          </div>
                        )}
                        {formData.payment_status === 'paid' && (
                          <div className="flex items-center justify-center mt-2 p-2 bg-green-100 rounded-lg">
                            <svg className="w-4 h-4 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-sm font-semibold text-green-700">{t('salesOrders.paidInFull')}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-bold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('common.loading')}
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {id ? t('common.update') : t('common.create')}
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/sales-orders')}
                    className="w-full px-6 py-4 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all duration-200 font-semibold"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Modals */}
      <AlertModal
        isOpen={alert.isOpen}
        onClose={closeAlert}
        title={alert.title}
        message={alert.message}
        type={alert.type}
      />

      <MeasurementModal
        isOpen={measurementModal.isOpen}
        onClose={handleCloseMeasurementModal}
        onSave={handleSaveMeasurements}
        itemName={measurementModal.itemName}
        existingMeasurements={measurementModal.itemIndex !== null ? items[measurementModal.itemIndex]?.measurements : null}
        previousMeasurements={previousMeasurements}
      />

      <FeeSelectionModal
        isOpen={feeModalOpen}
        onClose={() => setFeeModalOpen(false)}
        fees={fees}
        selectedFeeId={formData.fee_id}
        onSelect={(feeId) => setFormData({ ...formData, fee_id: feeId })}
      />

      <DiscountSelectionModal
        isOpen={discountModalOpen}
        onClose={() => setDiscountModalOpen(false)}
        discounts={discounts}
        selectedDiscountId={formData.discount_id}
        onSelect={(discountId) => setFormData({ ...formData, discount_id: discountId })}
        isItemDiscount={false}
      />

      <DiscountSelectionModal
        isOpen={itemDiscountModal.isOpen}
        onClose={() => setItemDiscountModal({ isOpen: false, itemIndex: null })}
        discounts={discounts}
        selectedDiscountId={currentItem.discount_id}
        onSelect={(discountId) => setCurrentItem({ ...currentItem, discount_id: discountId })}
        isItemDiscount={true}
      />
    </div>
  );
};

export default SalesOrderForm;
